import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from './ui/button';
import { Moon, Sun } from 'lucide-react';

type ThemeMode = 'light' | 'dark';

const getInitialTheme = (): ThemeMode => {
  try {
    const savedTheme = localStorage.getItem('theme') as ThemeMode | null;
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }

    const systemPrefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    return systemPrefersDark ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const applyThemeToRoot = (theme: ThemeMode) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export const ModernThemeToggle = () => {
  // IMPORTANT: initialize from localStorage to avoid flashing light->dark on mount
  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme());
  const { translate } = useTranslation();

  const hasUserPreference = useMemo(() => {
    try {
      return localStorage.getItem('theme') === 'dark' ||
        localStorage.getItem('theme') === 'light';
    } catch {
      return false;
    }
  }, []);

  // Apply theme on mount & when it changes.
  // This is safe now because our initial state already matches localStorage/system.
  useEffect(() => {
    try {
      applyThemeToRoot(theme);
      localStorage.setItem('theme', theme);
    } catch {
      // ignore
    }
  }, [theme]);

  // Optional: follow system theme changes only if user hasn't explicitly set one.
  useEffect(() => {
    if (hasUserPreference) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [hasUserPreference]);

  const handleToggle = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleToggle}
      aria-label={translate('pos.common.action.toggle_theme')}
      title={translate('pos.common.action.toggle_theme')}
    >
      {theme === 'dark' ? (
        <Sun className="h-5 w-5" />
      ) : (
        <Moon className="h-5 w-5" />
      )}
    </Button>
  );
};
